import { useEffect, useRef } from "react";
import PropTypes from "prop-types";

const SCRIPT_ID = "cloudflare-turnstile-script";
const DEVELOPMENT_SITE_KEY = "1x00000000000000000000AA";

const loadTurnstile = () =>
  new Promise((resolve, reject) => {
    if (window.turnstile) {
      resolve(window.turnstile);
      return;
    }

    const existingScript = document.getElementById(SCRIPT_ID);
    if (existingScript) {
      existingScript.addEventListener("load", () => resolve(window.turnstile), {
        once: true,
      });
      existingScript.addEventListener("error", reject, { once: true });
      return;
    }

    const script = document.createElement("script");
    script.id = SCRIPT_ID;
    script.src =
      "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
    script.async = true;
    script.defer = true;
    script.onload = () => resolve(window.turnstile);
    script.onerror = reject;
    document.head.appendChild(script);
  });

const TurnstileWidget = ({ onVerify, resetKey }) => {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const onVerifyRef = useRef(onVerify);

  useEffect(() => {
    onVerifyRef.current = onVerify;
  }, [onVerify]);

  useEffect(() => {
    let cancelled = false;
    const siteKey =
      process.env.REACT_APP_TURNSTILE_SITE_KEY ||
      (process.env.NODE_ENV !== "production" ? DEVELOPMENT_SITE_KEY : "");

    if (!siteKey) {
      onVerifyRef.current("");
      return undefined;
    }

    loadTurnstile()
      .then((turnstile) => {
        if (cancelled || !containerRef.current) return;

        widgetIdRef.current = turnstile.render(containerRef.current, {
          sitekey: siteKey,
          action: "registration",
          theme: "auto",
          callback: (token) => onVerifyRef.current(token),
          "expired-callback": () => onVerifyRef.current(""),
          "error-callback": () => onVerifyRef.current(""),
        });
      })
      .catch(() => onVerifyRef.current(""));

    return () => {
      cancelled = true;
      if (widgetIdRef.current !== null && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (widgetIdRef.current !== null && window.turnstile) {
      window.turnstile.reset(widgetIdRef.current);
      onVerifyRef.current("");
    }
  }, [resetKey]);

  return <div ref={containerRef} className="d-flex justify-content-center" />;
};

TurnstileWidget.propTypes = {
  onVerify: PropTypes.func.isRequired,
  resetKey: PropTypes.number.isRequired,
};

export default TurnstileWidget;
